import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import html2canvas from 'html2canvas';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { 
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle 
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { Download, Loader2, Eye, FileImage } from 'lucide-react';
import { LocationSlideTemplate } from './LocationSlideTemplate';

interface LocationSlideGeneratorProps {
  locationId: string;
  isOpen: boolean;
  onClose: () => void;
}

export function LocationSlideGenerator({ locationId, isOpen, onClose }: LocationSlideGeneratorProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { userRole } = useAuth();
  const isChargingUser = userRole === 'charging_user';
  const slideRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [previewScale, setPreviewScale] = useState(0.4);
  const [showMissingDrawingAlert, setShowMissingDrawingAlert] = useState(false);

  // Fetch Mapbox token from edge function
  const { data: mapboxToken } = useQuery({
    queryKey: ['mapbox-token'],
    queryFn: async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-mapbox-token');
        if (error) {
          console.error('Failed to fetch Mapbox token:', error);
          return null;
        }
        return data?.token || null;
      } catch (err) {
        console.error('Mapbox token fetch error:', err);
        return null;
      }
    },
    enabled: isOpen,
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  });

  // Fetch location data (including site_sketch_url)
  const { data: location, isLoading: loadingLocation } = useQuery({
    queryKey: ['slide-location', locationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('charging_locations')
        .select('*')
        .eq('id', locationId)
        .single();
      
      if (error) throw error;
      return data as any; // includes site_sketch_url
    },
    enabled: isOpen && !!locationId,
  });

  // Fetch interested parties
  const { data: parties = [] } = useQuery({
    queryKey: ['slide-parties', locationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('opportunity_locations')
        .select(`
          id,
          opportunity:charging_opportunities(
            id,
            name,
            organization:organizations(id, name, logo_url, favicon_url, cached_favicon_url)
          )
        `)
        .eq('location_id', locationId);
      
      if (error) throw error;
      
      return (data || []).map((link: any) => ({
        orgName: link.opportunity?.organization?.name || link.opportunity?.name || 'Onbekend',
        orgLogo: link.opportunity?.organization?.cached_favicon_url || 
                 link.opportunity?.organization?.logo_url || 
                 link.opportunity?.organization?.favicon_url,
      })).filter((p: any) => p.orgName !== 'Onbekend');
    },
    enabled: isOpen && !!locationId,
  });

  // Use site_sketch_url directly from location data (no longer fetching from documents)
  const siteDrawing = location?.site_sketch_url || null;

  const handleExport = async () => {
    if (!slideRef.current || !location) {
      toast({
        title: 'Export mislukt',
        description: 'Slide template niet gevonden',
        variant: 'destructive',
      });
      return;
    }

    // Check if there's no drawing attached
    if (!siteDrawing) {
      setShowMissingDrawingAlert(true);
      return;
    }

    await performExport();
  };

  // Wait for all images in the container to load
  const waitForAssets = async (container: HTMLElement): Promise<void> => {
    const images = container.querySelectorAll('img');
    console.log(`[SlideGenerator] Waiting for ${images.length} images to load...`);
    
    const loadPromises = Array.from(images).map((img, idx) => {
      return new Promise<void>((resolve) => {
        if (img.complete && img.naturalHeight !== 0) {
          console.log(`[SlideGenerator] Image ${idx} already loaded:`, img.src?.substring(0, 80));
          resolve();
          return;
        }
        
        const timeout = setTimeout(() => {
          console.warn(`[SlideGenerator] Image ${idx} timed out:`, img.src?.substring(0, 80));
          resolve();
        }, 5000);
        
        img.onload = () => {
          clearTimeout(timeout);
          console.log(`[SlideGenerator] Image ${idx} loaded:`, img.src?.substring(0, 80));
          resolve();
        };
        img.onerror = () => {
          clearTimeout(timeout);
          console.error(`[SlideGenerator] Image ${idx} failed to load:`, img.src?.substring(0, 80));
          resolve();
        };
      });
    });
    
    await Promise.all(loadPromises);
    console.log('[SlideGenerator] All images processed');
  };

  const performExport = async () => {
    if (!slideRef.current || !location) {
      console.error('[SlideGenerator] Missing slideRef or location');
      return;
    }
    
    setIsExporting(true);
    setShowMissingDrawingAlert(false);

    const exportContext = {
      locationId,
      locationName: location.name,
      locode: location.locode,
      hasCoords: !!(location.latitude && location.longitude),
      hasSiteDrawing: !!siteDrawing,
      siteDrawingUrl: siteDrawing?.substring(0, 100),
      hasMapboxToken: !!mapboxToken,
      partiesCount: parties.length,
    };
    console.log('[SlideGenerator] Starting export with context:', exportContext);

    try {
      // Wait for all images to load before capturing
      await waitForAssets(slideRef.current);
      
      console.log('[SlideGenerator] Capturing with html2canvas...');
      
      // Create canvas from the slide element
      const canvas = await html2canvas(slideRef.current, {
        scale: 1, // Already 1920x1080, no need to scale up
        useCORS: true,
        allowTaint: false, // Changed to false for better CORS handling
        backgroundColor: '#080c55',
        logging: false,
      });

      console.log('[SlideGenerator] Canvas created:', {
        width: canvas.width,
        height: canvas.height,
      });

      // Convert to blob and download with proper error handling
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((b) => resolve(b), 'image/png', 1.0);
      });
      
      if (!blob) {
        throw new Error('Failed to create image blob - canvas.toBlob returned null');
      }

      const link = document.createElement('a');
      const fileName = `${location.locode || 'locatie'}-${location.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'slide'}.png`;
      link.download = fileName;
      link.href = URL.createObjectURL(blob);
      link.click();
      URL.revokeObjectURL(link.href);

      console.log('[SlideGenerator] Export successful:', fileName);

      toast({
        title: 'Slide geëxporteerd',
        description: `${fileName} is gedownload`,
      });
    } catch (error: any) {
      console.error('[SlideGenerator] Export error:', error);
      console.error('[SlideGenerator] Error context:', exportContext);
      console.error('[SlideGenerator] Error stack:', error?.stack);
      toast({
        title: 'Export mislukt',
        description: error.message || 'Er ging iets mis bij het exporteren',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleNavigateToDocuments = () => {
    setShowMissingDrawingAlert(false);
    onClose();
    // Route based on user role: charging users go to /charging, CRM users go to /crm/charging
    const basePath = isChargingUser ? '/charging' : '/crm/charging';
    navigate(`${basePath}/locatie/${locationId}?tab=documenten`);
  };

  if (!isOpen) return null;

  const isLoading = loadingLocation;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Locatie Slide Generator
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : location ? (
            <div className="space-y-4">
              {/* Preview Controls */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Preview schaal:</span>
                  <Button
                    variant={previewScale === 0.3 ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPreviewScale(0.3)}
                  >
                    30%
                  </Button>
                  <Button
                    variant={previewScale === 0.4 ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPreviewScale(0.4)}
                  >
                    40%
                  </Button>
                  <Button
                    variant={previewScale === 0.5 ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPreviewScale(0.5)}
                  >
                    50%
                  </Button>
                </div>

                <Button onClick={handleExport} disabled={isExporting}>
                  {isExporting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Exporteren...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Exporteer als PNG
                    </>
                  )}
                </Button>
              </div>

              {/* Preview Container (scaled for display) */}
              <div 
                className="border rounded-lg overflow-auto bg-muted/30 p-4"
                style={{ maxHeight: 'calc(80vh - 200px)' }}
              >
                <div 
                  style={{ 
                    transform: `scale(${previewScale})`,
                    transformOrigin: 'top left',
                    width: `${1920 * previewScale}px`,
                    height: `${1080 * previewScale}px`,
                  }}
                >
                  {/* Preview version - no ref */}
                  <LocationSlideTemplate
                    location={location}
                    interestedParties={parties}
                    siteDrawingUrl={siteDrawing}
                    mapboxToken={mapboxToken}
                  />
                </div>
              </div>

              {/* Hidden full-size export container - this is what html2canvas captures */}
              <div 
                style={{ 
                  position: 'absolute',
                  left: '-9999px',
                  top: 0,
                  width: '1920px',
                  height: '1080px',
                }}
              >
                <LocationSlideTemplate
                  ref={slideRef}
                  location={location}
                  interestedParties={parties}
                  siteDrawingUrl={siteDrawing}
                  mapboxToken={mapboxToken}
                />
              </div>

              {/* Info */}
              <div className="text-sm text-muted-foreground">
                <p>De slide wordt geëxporteerd als 1920x1080 PNG (16:9 PowerPoint formaat).</p>
                <p>Tip: Upload een tekening als document type "Tekening" om die hier te tonen.</p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              Locatie niet gevonden
            </div>
          )}
        </div>
      </DialogContent>

      {/* Missing Drawing Alert */}
      <AlertDialog open={showMissingDrawingAlert} onOpenChange={setShowMissingDrawingAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <FileImage className="h-5 w-5 text-amber-500" />
              Geen tekening gekoppeld
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Deze locatie heeft nog geen tekening (site layout) gekoppeld. 
                De slide zal worden gegenereerd zonder tekening afbeelding.
              </p>
              <div className="bg-muted p-3 rounded-lg text-sm">
                <p className="font-medium mb-2">Om een tekening toe te voegen:</p>
                <ol className="list-decimal ml-4 space-y-1">
                  <li>Ga naar de documenten tab van deze locatie</li>
                  <li>Upload een document met type "Tekening"</li>
                  <li>De meest recente tekening wordt automatisch op de slide getoond</li>
                </ol>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction 
              onClick={(e) => {
                e.preventDefault(); // Prevent dialog close race condition
                console.log('[SlideGenerator] "Negeer & Download" clicked');
                performExport();
              }}
              className="bg-secondary text-secondary-foreground hover:bg-secondary/80"
            >
              Negeer & Download
            </AlertDialogAction>
            <AlertDialogAction onClick={handleNavigateToDocuments}>
              Voeg tekeningen toe
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
